import React, { useEffect, useState } from 'react';
import { ActivityIndicator, ScrollView, StatusBar, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { collection, doc, onSnapshot, orderBy, query, updateDoc, where } from 'firebase/firestore';
import { Colors, Radius, Shadow, Typography } from '../theme';
import { auth, firestore } from '../firebase';
import { LANGUAGE_LOCALES, useI18n } from '../i18n';

type PartnerNotification = {
  id: string;
  title: string;
  message: string;
  read: boolean;
  createdAt?: { toDate?: () => Date } | null;
};

export default function PartnerNotificationsScreen() {
  const insets = useSafeAreaInsets();
  const { language, t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState<PartnerNotification[]>([]);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setLoading(false);
      return undefined;
    }

    const unsubscribe = onSnapshot(
      query(
        collection(firestore, 'partnerNotifications'),
        where('partnerUid', '==', uid),
        orderBy('createdAt', 'desc'),
      ),
      snapshot => {
        setNotifications(snapshot.docs.map(notificationDoc => ({
          id: notificationDoc.id,
          ...(notificationDoc.data() as Omit<PartnerNotification, 'id'>),
        })));
        setLoading(false);
      },
      error => {
        console.warn('[Tonyte][PartnerNotifications] subscription failed', error);
        setNotifications([]);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  const markRead = (notificationId: string) => {
    void updateDoc(doc(firestore, 'partnerNotifications', notificationId), { read: true }).catch(() => undefined);
  };

  return (
    <LinearGradient colors={['#0B1826', '#070B14', '#020409']} style={styles.container}>
      <StatusBar barStyle="light-content" translucent backgroundColor="transparent" />
      <ScrollView contentContainerStyle={[styles.scroll, { paddingTop: insets.top + 24, paddingBottom: insets.bottom + 40 }]}>
        <Text style={styles.title}>{t('partner.notifications')}</Text>

        {loading ? (
          <ActivityIndicator size="large" color="#FF7A00" style={styles.loader} />
        ) : notifications.length === 0 ? (
          <Text style={styles.emptyText}>{t('partner.noNotifications')}</Text>
        ) : (
          notifications.map(notification => (
            <TouchableOpacity
              key={notification.id}
              style={[styles.card, !notification.read && styles.cardUnread]}
              activeOpacity={0.85}
              onPress={() => markRead(notification.id)}
            >
              <View style={styles.cardHeader}>
                <Text style={styles.cardTitle}>{notification.title}</Text>
                {!notification.read && <View style={styles.unreadDot} />}
              </View>
              <Text style={styles.cardMessage}>{notification.message}</Text>
              {notification.createdAt?.toDate && (
                <Text style={styles.cardTime}>{notification.createdAt.toDate().toLocaleString(LANGUAGE_LOCALES[language])}</Text>
              )}
            </TouchableOpacity>
          ))
        )}
      </ScrollView>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Colors.bg },
  scroll: { paddingHorizontal: 20 },
  loader: { marginTop: 60 },
  title: {
    color: Colors.textPrimary,
    fontSize: Typography.xxl,
    fontWeight: Typography.extrabold,
    marginBottom: 20,
  },
  emptyText: {
    color: Colors.textMuted,
    fontSize: Typography.sm,
    lineHeight: 21,
  },
  card: {
    padding: 14,
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(9,17,29,0.92)',
    marginBottom: 10,
    ...Shadow.strong,
  },
  cardUnread: {
    borderColor: 'rgba(255,122,0,0.4)',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardTitle: {
    flex: 1,
    color: Colors.textPrimary,
    fontSize: Typography.base,
    fontWeight: Typography.semibold,
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#FF7A00',
    marginLeft: 8,
  },
  cardMessage: {
    marginTop: 6,
    color: Colors.textSecondary,
    fontSize: Typography.sm,
    lineHeight: 20,
  },
  cardTime: {
    marginTop: 8,
    color: Colors.textMuted,
    fontSize: Typography.xs,
  },
});
